import { useEffect, useRef, useState } from "react";
import { usePreferences } from "../preferences/usePreferences";
import { useI18n } from "../i18n/I18nProvider";
import { localizeError } from "../auth/localizeError";
import { logoForTheme } from "../theme/brand";
import { Ic } from "../../components/Icons.jsx";
import "./onboarding.css";

const steps = ["language_appearance", "profile"] as const;

export function Onboarding() {
  const { profile, locale, theme, save, submitOnboarding, busy, error: preferenceError } = usePreferences();
  const { t } = useI18n();
  const state = profile!.onboarding!;
  // Keep the persisted step IDs compatible; the retired profile step is a tutorial.
  const step = state.step === "profile" || state.step === "data_choices" ? "profile" : "language_appearance";
  const index = steps.indexOf(step);
  const [error, setError] = useState<unknown>(null);
  const [working, setWorking] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const locked = busy || working;
  useEffect(() => { heading.current?.focus(); }, [step]);

  async function act(action: () => Promise<unknown>) {
    setWorking(true); setError(null);
    try { await action(); }
    catch (failure) { setError(failure); }
    finally { setWorking(false); }
  }
  function move(next: typeof steps[number]) {
    return act(() => submitOnboarding("draft", { step: next, draft: {} }));
  }
  function complete() {
    return act(() => submitOnboarding("complete", {}));
  }
  const titles = [t("语言与外观", "Language and appearance"), t("欢迎使用", "Welcome")];
  return <main className="setup-page">
    <section className="setup-shell" aria-labelledby="onboarding-title">
      <header className="setup-header">
        <img src={logoForTheme(theme)} alt="Lyncia" />
        <span>{t(`步骤 ${index + 1} / 2`, `Step ${index + 1} of 2`)}</span>
      </header>
      <ol className="setup-progress" aria-label={t("引导进度", "Setup progress")}>
        {titles.map((title, i) => <li key={i} aria-current={index === i ? "step" : undefined} data-complete={i < index}>
          <span className="setup-step-number" aria-hidden="true">{i < index ? "✓" : i + 1}</span><span>{title}</span>
        </li>)}
      </ol>
      <div className="setup-intro">
        {index === 0 && <p className="setup-eyebrow">{t("欢迎来到 Lyncia", "Welcome to Lyncia")}</p>}
        <h1 id="onboarding-title" ref={heading} tabIndex={-1}>{index === 0 ? titles[index] : t("欢迎使用 Lyncia", "Welcome to Lyncia")}</h1>
        <p>{step === "language_appearance"
          ? t("让这里更合你的习惯。之后也可以随时在设置中调整。", "Make this space feel right for you. You can change these choices in Settings anytime.")
          : t("选择适合你的交流方式。", "Choose the way you would like to connect.")}</p>
      </div>
      <form onSubmit={e => { e.preventDefault(); if (!locked) void (index === 0 ? move("profile") : complete()); }}>
        {(error || preferenceError) && <p role="alert" className="notice error">{error ? localizeError(error, locale) : preferenceError}</p>}
        {step === "language_appearance" ? <div className="setup-fields">
          <fieldset disabled={locked}>
            <legend>{t("语言", "Language")}</legend>
            <div className="setup-options">
              {([['zh-CN', '简体中文', 'Chinese'], ['en', 'English', '英语']] as const).map(([value, label, detail]) =>
                <label className="setup-choice" key={value}>
                  <input type="radio" name="locale" value={value} checked={locale === value} onChange={() => void act(() => save({ locale: value }))} />
                  <span><strong>{label}</strong><small>{detail}</small></span>
                </label>)}
            </div>
          </fieldset>
          <fieldset disabled={locked}>
            <legend>{t("外观", "Appearance")}</legend>
            <div className="setup-options">
              {([['light', t('浅色', 'Light')], ['dark', t('深色', 'Dark')]] as const).map(([value, label]) =>
                <label className="setup-choice setup-appearance" key={value}>
                  <input type="radio" name="theme" value={value} checked={theme === value} onChange={() => void act(() => save({ preferences: { theme: value } }))} />
                  <span className="setup-theme-icon" aria-hidden="true">{value === "light" ? <Ic.Sun size={22} /> : <Ic.Moon size={22} />}</span>
                  <strong>{label}</strong>
                </label>)}
            </div>
          </fieldset>
        </div> : <div className="setup-fields setup-features">
          <article className="setup-feature" aria-labelledby="setup-chat-title">
            <span className="setup-feature-icon" aria-hidden="true"><Ic.Bot size={22} /></span>
            <h2 id="setup-chat-title">{t("直接对话", "Direct conversation")}</h2>
            <p>{t("围绕当下的想法、感受或困扰，随时开启一段对话。", "Start a conversation about what is on your mind, how you feel, or what is troubling you.")}</p>
          </article>
          <article className="setup-feature" aria-labelledby="setup-therapy-title">
            <span className="setup-feature-icon" aria-hidden="true"><Ic.Sparkle size={22} /></span>
            <h2 id="setup-therapy-title">{t("多 session 心理咨询", "Multi-session counseling")}</h2>
            <p>{t("围绕一个想持续探索的话题，分多次进行咨询，结合之前的交流继续深入。", "Explore a topic across multiple sessions, building on your previous conversations.")}</p>
          </article>
        </div>}
        <footer className="setup-actions">
          <button type="button" className="quiet" disabled={locked} onClick={() => void act(() => submitOnboarding("skip", {}))}>{t("跳过引导", "Skip setup")}</button>
          <div>{index > 0 && <button type="button" className="secondary" disabled={locked} onClick={() => void move("language_appearance")}>{t("上一步", "Back")}</button>}
            <button type="submit" className="primary" disabled={locked}>{locked ? t("正在保存…", "Saving…") : index === 0 ? t("下一步", "Continue") : t("开始使用", "Start using Lyncia")}</button>
          </div>
        </footer>
      </form>
    </section>
  </main>;
}
